import moment from 'moment-timezone';

import { PredefinedMilisecondsTypeEnum } from '../enums';
import { MarketCountryCode, NonWorkingDaysResponse } from '../models/fundamental/types';
import { MarketState } from '../models/market-data/types';
import MarketHoursState from '../store-util/MarketHoursState';

import { calculateMarketState, convertToMarketState } from './temporalDeadZoneFunctions';

const LOOKAHEAD_DAYS = 8;
const LOOKAHEAD_MINUTES = LOOKAHEAD_DAYS * 24 * 60;
const TRANSITION_BUFFER_MS = 150;

/**
 * Probing every minute over the whole look-ahead means up to `LOOKAHEAD_MINUTES` market-state
 * computations per call. Probe coarsely first, then refine minute by minute inside the window that
 * brackets the change - that is ~780 probes instead of 11520 for the same answer.
 *
 * This assumes no market session is shorter than the coarse step, which holds for every session in
 * `marketHours` (the shortest real one is a half-day after-market at four hours). If a config ever
 * broke that assumption the transition timer would fire late rather than wrongly, and the 30-second
 * safety interval in `useMarketState` would still pick the new state up.
 */
const COARSE_PROBE_MINUTES = 15;

/**
 * `useMarketState` re-derives this on a 30-second interval, on window focus and on visibility
 * change - and it is mounted once per table row, so the same answer was being recomputed hundreds of
 * times a minute. The next transition for a country does not move until it passes, so cache the
 * absolute timestamp and just subtract until then.
 */
type TransitionCacheEntry = {
  marketHoursSource: NonWorkingDaysResponse
  transitionAtMs: number
}

const transitionCache = new Map<string, TransitionCacheEntry>();

/** Exposed for tests and for callers that change the wall clock or market hours out of band. */
export function invalidateMarketStateTransitionCache() {
  transitionCache.clear();
}

const findFirstTransitionOffset = (
  startMinute: moment.Moment,
  currentState: MarketState,
  countryCode: string,
): number | null => {
  const stateAtOffset = (offset: number) => {
    const probeIso = startMinute.clone().add(offset, 'minute').toISOString();
    const snapshot = calculateMarketState(probeIso, countryCode);

    return snapshot == null ? null : convertToMarketState(snapshot);
  };

  for (let coarseOffset = COARSE_PROBE_MINUTES; ; coarseOffset += COARSE_PROBE_MINUTES) {
    const boundedOffset = Math.min(coarseOffset, LOOKAHEAD_MINUTES);
    const coarseState = stateAtOffset(boundedOffset);

    if (coarseState != null && coarseState !== currentState) {
      const windowStart = Math.max(boundedOffset - COARSE_PROBE_MINUTES + 1, 1);
      for (let offset = windowStart; offset <= boundedOffset; offset += 1) {
        const probeState = stateAtOffset(offset);
        if (probeState != null && probeState !== currentState) {
          return offset;
        }
      }

      return boundedOffset;
    }

    if (boundedOffset >= LOOKAHEAD_MINUTES) {
      return null;
    }
  }
};

/**
 * Returns the delay (in ms) until the next market-state transition for a country.
 * Uses `calculateMarketState` for both "now" and future probes to keep transition logic centralized.
 */
export const getNextMarketStateTransitionDelay = (
  countryCode: string = MarketCountryCode.UnitedStates,
  nowUtcIso?: string,
): number | null => {
  const nowUtc = nowUtcIso ? moment.utc(nowUtcIso) : moment.utc();

  if (!nowUtc.isValid()) {
    return null;
  }

  const nowMs = nowUtc.valueOf();
  const marketHoursSource = MarketHoursState.get(countryCode);
  // A caller-supplied "now" must never be served from - or written to - the cache.
  const cached = nowUtcIso ? undefined : transitionCache.get(countryCode);

  if (cached && cached.marketHoursSource === marketHoursSource && cached.transitionAtMs > nowMs) {
    return Math.max(cached.transitionAtMs - nowMs, PredefinedMilisecondsTypeEnum.oneSecond);
  }

  const currentSnapshot = calculateMarketState(nowUtc.toISOString(), countryCode);
  if (currentSnapshot == null) {
    return null;
  }
  const currentState = convertToMarketState(currentSnapshot);
  if (currentState == null) {
    return null;
  }

  const startMinute = nowUtc.clone().startOf('minute');
  const transitionOffset = findFirstTransitionOffset(startMinute, currentState, countryCode);

  if (transitionOffset == null) {
    return null;
  }

  const transitionAtMs = startMinute.clone().add(transitionOffset, 'minute').valueOf() + TRANSITION_BUFFER_MS;

  if (!nowUtcIso) {
    transitionCache.set(countryCode, { marketHoursSource, transitionAtMs });
  }

  return Math.max(transitionAtMs - nowMs, PredefinedMilisecondsTypeEnum.oneSecond);
};
